import type { I18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import * as z from "zod/v4";

/**
 * Single source of truth for what makes a password acceptable. `PasswordRequirements` renders these
 * as a live checklist and `createPasswordSchema` validates against the same predicates, so the list
 * a parker sees can never drift from what the form actually enforces.
 */
export const PASSWORD_RULES = [
	{
		id: "min-length",
		label: msg({ message: "8 or more characters required", comment: "Password requirement checklist item" }),
		isMet: (password: string) => password.length >= 8,
	},
	{
		id: "uppercase",
		label: msg({ message: "One uppercase letter required", comment: "Password requirement checklist item" }),
		isMet: (password: string) => /[A-Z]/.test(password),
	},
	{
		id: "special-character",
		label: msg({ message: "One special character required", comment: "Password requirement checklist item" }),
		// Letters, digits and whitespace don't count — a trailing space must not read as a symbol
		isMet: (password: string) => /[^\p{L}\p{N}\s]/u.test(password),
	},
] as const;

export const createPasswordSchema = ({ i18n }: { i18n: I18n }): z.ZodType<string> =>
	PASSWORD_RULES.reduce<z.ZodType<string>>(
		(schema, rule) => schema.refine(rule.isMet, { message: i18n._(rule.label) }),
		z.string(),
	);
